import { StatusFilterState, statusFilterConstrains } from './external-filters'

/**
 * The status checkboxes (Unmatched / Ignored / Matched), as the SERVER-side half of their predicate.
 *
 * The checkboxes filter on BOTH sides. Server-side they narrow the `/query` page through the static
 * `matchStatus` column — a `SetFilter<String>` slot on both `FlexTransactionsRequestFilters` and
 * `FlexExternalTransactionsRequestFilters` — so the bounded page holds the right rows for the whole
 * session rather than whatever the first 5000 happened to be. Client-side the existing external
 * filter still runs over the returned page, because `MatchingService.statusOf` layers the optimistic
 * status of a match / unmatch / ignore the user just performed on top of the server's verdict, and
 * that has to show before the backend catches up.
 */

/** The static column the checkboxes constrain. Declared on both read models, catalogued or not. */
export const MATCH_STATUS_FIELD = 'matchStatus'

/**
 * The `matchStatus` value each checkbox stands for — the same vocabulary positions-2's chips send.
 *
 * flex-transactions writes only UNMATCHED and MATCHED today (`FlexMatchStatus` in
 * `services/flex-transactions/.../models/MatchColumns.kt`); Ignore is local-only until PVAL-26309
 * persists it. IGNORED is still sent when Ignored is checked: the slot is a `SetFilter<String>`, so an
 * unknown value is not an error, it just matches nothing yet — and the request already says the right
 * thing for the day the backend starts writing it.
 */
export const SERVER_MATCH_STATUS = {
  unmatched: 'UNMATCHED',
  matched: 'MATCHED',
  ignored: 'IGNORED',
} as const

/**
 * The `matchStatus` values each checkbox admits. Unmatched also admits `HELD`: hold has no tick-box
 * of its own and answers to Unmatched on screen (`STATUS_FACET` in `external-filters.ts`), so the
 * request has to ask for it too — otherwise a held row is dropped from the page under the default
 * Unmatched-only filter before the client-side pass ever sees it.
 */
const SERVER_VALUES_OF: Record<
  keyof StatusFilterState,
  ReadonlyArray<string>
> = {
  unmatched: [SERVER_MATCH_STATUS.unmatched, 'HELD'],
  matched: [SERVER_MATCH_STATUS.matched],
  ignored: [SERVER_MATCH_STATUS.ignored],
}

/** Checkbox order, so the value list (and the refetch snapshot) does not depend on click order. */
const ORDER: ReadonlyArray<keyof StatusFilterState> = [
  'unmatched',
  'matched',
  'ignored',
]

/**
 * The `matchStatus` values the checkboxes allow, or `null` when they must not constrain the server
 * at all.
 *
 *  - **All three checked → `null`**, not the three values. "Every status" has to be the absence of a
 *    filter, or a row whose status is absent or is a value the backend adds later would be silently
 *    dropped — the same rule positions-2's chips follow.
 *  - **Nothing checked → `null` too.** No checkbox ticked means no status filter, not "show nothing";
 *    the client-side pass reads it the same way (`statusFilterConstrains`).
 *  - **Otherwise exactly the checked statuses**, with held rows riding on Unmatched. Unmatched +
 *    Matched is `['UNMATCHED', 'HELD', 'MATCHED']`, which correctly leaves out ignored rows once the
 *    backend has any.
 *
 * A locally-ignored row does not survive the refetch a toggle triggers anyway (a refetch replaces the
 * row objects, and Ignore is not persisted yet), so there is nothing to keep reachable by leaving the
 * request unconstrained.
 */
export function serverMatchStatusValues(
  status: StatusFilterState,
): string[] | null {
  if (status.unmatched && status.matched && status.ignored) return null
  if (!statusFilterConstrains(status)) return null
  return ORDER.filter(key => status[key]).flatMap(key => SERVER_VALUES_OF[key])
}
